// @flow
import * as React from 'react';
import {useHistory} from "react-router-dom";
import styled from 'styled-components';
import {BackgroundImage} from 'components/Image';
import {LayoutLeft} from 'components/Layout';
import {LocalizedParagraph, useLocalized} from 'components/Localized';
import ImgListUp from 'assets/images/driving-zone/b/btn-usplist-up.svg';
import ImgListDown from 'assets/images/driving-zone/b/btn-usplist-down.svg';
import ImgListFull from 'assets/images/driving-zone/b/img-usplist-full.svg';

function getNearestLink(links: Array<string>, section: string, page: number): number {
  const selectedLinks = links.filter(link => link.includes(section));
  const selectedPages = selectedLinks.map(link => parseInt(link.substring(link.indexOf('=') + 1)));
  const closestPage = selectedPages.reduce(function(prev, curr) {
    return (Math.abs(curr - page) < Math.abs(prev - page) ? curr : prev);
  });
  
  if (closestPage != null && !isNaN(closestPage)) {
    let result = 0;
    links.forEach((link, index) => {
      if (link.includes(`=${closestPage}`) && link.includes(section)) {
        result = index;
      }
    })
    return result;
  }
  return 0;
}

type Props = {
  section: string,
  page: number,
  onClose: () => void,
  onClick: Array<?((number) => void)>,
};

function tagNavigationIndex(idx:number) : void {
  switch(idx) {
    case 0:
    window.tagManager.navigationCurvedDisplayLinkClick();
    break;
    case 1:
    window.tagManager.navigationPremiumSoundLinkClick();
    break;
    case 2:
    window.tagManager.navigationEcoMaterialLinkClick();
    break;
    case 3:
    window.tagManager.navigationDriveModesLinkClick();
    break;
    case 4:
    window.tagManager.navigationDrivingAssistantLinkClick();
    break;
    case 5:
    window.tagManager.navigationParkingAssistLinkClick();
    break;
    default:
    break;
  }
}

export default function MenuUSP(props: Props): React.Element<any> {
  const history = useHistory();
  const localized = useLocalized();
  const menus = React.useMemo(
    () => [
      localized.driving_b03_guide_061,
      localized.driving_b03_guide_062,
      localized.driving_b03_guide_063,
      localized.driving_b03_guide_064,
      localized.driving_b03_guide_065,
      localized.driving_b03_guide_066,
    ],
    [localized]
  );
  const links = React.useMemo(
    () => [
      '/driving-zone/course-b/in-car-experience?goto=2',
      '/driving-zone/course-b/in-car-experience?goto=4',
      '/driving-zone/course-b/in-car-experience?goto=9',
      '/driving-zone/course-b/driving-experience?goto=6',
      '/driving-zone/course-b/driving-experience?goto=12',
      '/driving-zone/course-b/driving-experience?goto=16',
    ],
    []
  );
  const numbers = React.useMemo(
    () => [
      localized.driving_b03_guide_071,
      localized.driving_b03_guide_072,
      localized.driving_b03_guide_073,
      localized.driving_b03_guide_074,
      localized.driving_b03_guide_075,
      localized.driving_b03_guide_076,
    ],
    [localized]
  );
  const selectedNumbers = React.useMemo(
    () => [
      localized.driving_b03_guide_081,
      localized.driving_b03_guide_082,
      localized.driving_b03_guide_083,
      localized.driving_b03_guide_084,
      localized.driving_b03_guide_085,
      localized.driving_b03_guide_086,
    ],
    [localized]
  );
  const [index, setIndex] = React.useState(getNearestLink(links, props.section, props.page));
  const prevIndex = React.useMemo<number>(() => {
    if (index >= 1) {
      return index - 1;
    }
    return menus.length - 1;
  }, [index, menus]);
  const nextIndex = React.useMemo(() => {
    if (index < menus.length - 1) {
      return index + 1;
    }
    return 0;
  }, [index, menus]);

  const {onClose, onClick} = props;

  const getPage = React.useCallback((idx) => {
    const pageIndex = links[idx].lastIndexOf("=");
    if (pageIndex < 0) {
      return null;
    }
    const pageRaw = links[idx].substr(pageIndex + 1);
    const page = parseInt(pageRaw);
    if (isNaN(page)) {
      return null;
    }
    return page;
  }, [links])

  const handlePrev = React.useCallback(() => {
    const page = getPage(prevIndex);
    //console.log("navigation:(0)page=", page, "links[prevIndex]=", links[prevIndex], prevIndex);
    tagNavigationIndex(prevIndex);
    if (onClick[prevIndex] && page) {
      onClick[prevIndex]?.(page);
    }
    else {
      history.push(links[prevIndex]);
    }
    onClose();
  }, [history, links, prevIndex, onClose, onClick, getPage]);

  const handleCurr = React.useCallback(() => {
    const page = getPage(index);
    //console.log("navigation:(1)page=", page, "links[index]", links[index], index);
    tagNavigationIndex(index);
    if (onClick[index] && page) {
      onClick[index]?.(page);
    }
    else {
      history.push(links[index]);
    }
    onClose();
  }, [history, links, index, onClose, onClick, getPage]);

  const handleNext = React.useCallback(() => {
    const page = getPage(nextIndex);
    //console.log("navigation:(2)page=", page, "links[nextIndex]", links[nextIndex], nextIndex);
    tagNavigationIndex(nextIndex);
    if (onClick[nextIndex] && page) {
      onClick[nextIndex]?.(page);
    }
    else {
      history.push(links[nextIndex]);
    }
    onClose();
  }, [history, links, nextIndex, onClose, onClick, getPage]);

  return (
    <ListMenuWrapper>
      <button onClick={() => setIndex(prevIndex)}>
        <img src={ImgListUp} alt="" />
      </button>
      <BackgroundImage source="./driving-zone/b/img-index-step-line-usp.png">
        <button onClick={handlePrev}>
          <UnselectedNumber>
            <LocalizedParagraph {...numbers[prevIndex]} />
          </UnselectedNumber>
        </button>
        <button onClick={handleCurr}>
          <SelectedNumber>
            <LocalizedParagraph {...selectedNumbers[index]} />
          </SelectedNumber>
        </button>
        <button onClick={handleNext}>
          <UnselectedNumber>
            <LocalizedParagraph {...numbers[nextIndex]} />
          </UnselectedNumber>
        </button>
        <button onClick={handlePrev}>
          <LocalizedParagraph {...menus[prevIndex]} />
        </button>
        <button onClick={handleCurr}>
          <LocalizedParagraph {...menus[index]} />
        </button>
        <button onClick={handleNext}>
          <LocalizedParagraph {...menus[nextIndex]} />
        </button>
      </BackgroundImage>
      <button onClick={() => setIndex(nextIndex)}>
        <img src={ImgListDown} alt="" />
      </button>
    </ListMenuWrapper>
  );
}

const ListMenuWrapper = styled(LayoutLeft)`
  height: calc(100% - ${(props) => (props.theme.layout.mobile ? 36 : 18)}px);
  padding-left: ${(props) => (props.theme.layout.mobile ? 38 : 80)}px;
  padding-bottom: ${(props) => (props.theme.layout.mobile ? 36 : 18)}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  > button {
    width: ${(props) => (props.theme.layout.mobile ? 25 : 50)}px;
    height: ${(props) => (props.theme.layout.mobile ? 14 + 20 : 27 + 40)}px;
    padding: ${(props) => (props.theme.layout.mobile ? 10 : 20)}px 0px;
  }

  > div {
    width: ${(props) => (props.theme.layout.mobile ? 24 : 48)}px;
    height: ${(props) => (props.theme.layout.mobile ? 124 : 248)}px;
    background-size: 100% 100%;
    position: relative;

    > :nth-child(1) {
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? -10 : 0)}px;
      top: ${(props) => (props.theme.layout.mobile ? -10 : 0)}px;
    }
    > :nth-child(2) {
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? -10 : 0)}px;
      top: calc(50% - ${(props) => (props.theme.layout.mobile ? 44 / 2 : 48 / 2)}px);
    }
    > :nth-child(3) {
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? -10 : 0)}px;
      bottom: ${(props) => (props.theme.layout.mobile ? -10 : 0)}px;
    }
    > :nth-child(4) {
      width: max-content;
      height: ${(props) => (props.theme.layout.mobile ? 24 : 48)}px;
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? 32 : 68)}px;
      top: ${(props) => (props.theme.layout.mobile ? 0 : 0)}px;
    }
    > :nth-child(5) {
      width: max-content;
      height: ${(props) => (props.theme.layout.mobile ? 24 : 48)}px;
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? 32 : 68)}px;
      top: calc(50% - ${(props) => (props.theme.layout.mobile ? 24 / 2 : 48 / 2)}px);
    }
    > :nth-child(6) {
      width: max-content;
      height: ${(props) => (props.theme.layout.mobile ? 24 : 48)}px;
      position: absolute;
      left: ${(props) => (props.theme.layout.mobile ? 32 : 68)}px;
      bottom: ${(props) => (props.theme.layout.mobile ? 0 : 0)}px;
    }
  }
`;

const UnselectedNumber = styled.div`
  width: ${(props) => (props.theme.layout.mobile ? 44 : 48)}px;
  height: ${(props) => (props.theme.layout.mobile ? 44 : 48)}px;
  p {
    line-height: ${(props) => (props.theme.layout.mobile ? 44 : 48)}px;
    text-align: center;
  }
`;

const SelectedNumber = styled(UnselectedNumber)`
  background-image: url(${ImgListFull});
  background-size: ${(props) => (props.theme.layout.mobile ? 17 : 36)}px
    ${(props) => (props.theme.layout.mobile ? 17 : 36)}px;
  background-repeat: no-repeat;
  background-position: center center;
  p {
    padding-top: ${(props) => (props.theme.layout.mobile ? 1 : 0)}px;
  }
`;
